// imports
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// API base URL
// When VITE_NWDC_S3_URL is unset (e.g. CI test job), fall back to the current
// origin so RTK Query constructs an absolute URL that MSW node can intercept.
// When VITE_NWDC_S3_URL is absent or empty (e.g. CI test job), convert it to an
// absolute URL using the current origin so Node's fetch can construct valid URLs
// that MSW's setupServer can intercept. Mirrors the pattern from the old modelsApi.ts.
const _s3Url = import.meta.env.VITE_NWDC_S3_URL;
const baseUrl = !_s3Url ? new URL('', location.origin).href : _s3Url;

// types
export type AwdVariable = {
  name: string;
  displayId: string;
  displayFrontEnd: boolean;
};

export type AwdModel = {
  sectorDisplayId: string;
  sectorName: string;
  displayId: string;
  name: string;
  startYear: string;
  startMonth: string;
  endYear: string;
  endMonth: string;
  displayFrontEnd: boolean;
  variables: AwdVariable[];
};

export type AwdConfig = {
  models: AwdModel[];
};

// Helper selectors

// Deduplicate models by sectorDisplayId, return { key, value }[]
export function selectSectors(models: AwdModel[]): { key: string; value: string }[] {
  const seen = new Set<string>();
  const result: { key: string; value: string }[] = [];
  for (const model of models) {
    if (!seen.has(model.sectorDisplayId)) {
      seen.add(model.sectorDisplayId);
      result.push({ key: model.sectorDisplayId, value: model.sectorName });
    }
  }
  return result.sort((a, b) => a.value.localeCompare(b.value));
}

// Filter models by sectorDisplayId and displayFrontEnd, return { key, value }[]
export function selectModelsBySector(
  models: AwdModel[],
  sectorId: string
): { key: string; value: string }[] {
  return models
    .filter((m) => m.sectorDisplayId === sectorId && m.displayFrontEnd)
    .map((m) => ({ key: m.displayId, value: m.name }))
    .sort((a, b) => a.value.localeCompare(b.value));
}

// Find model by displayId, filter variables by displayFrontEnd, return { key, value }[]
export function selectVariablesByModel(
  models: AwdModel[],
  modelId: string
): { key: string; value: string }[] {
  const model = models.find((m) => m.displayId === modelId);
  if (!model) return [];
  return model.variables
    .filter((v) => v.displayFrontEnd)
    .map((v) => ({ key: v.displayId, value: v.name }))
    .sort((a, b) => a.value.localeCompare(b.value));
}

// Find a model by displayId
export function selectModelById(
  models: AwdModel[],
  modelId: string
): AwdModel | undefined {
  return models.find((m) => m.displayId === modelId);
}

// create API
export const awdConfigApi = createApi({
  reducerPath: 'awdConfigApi',
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (build) => ({
    getAwdConfig: build.query<AwdConfig, void>({
      query: () => '/config/otherWebPages/ssdt.json',
    }),
  }),
});

export const { useGetAwdConfigQuery } = awdConfigApi;
export default awdConfigApi;
